import { FC, useContext, useCallback, WheelEventHandler, useState } from 'react'
import { Box, Container, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import gql from 'graphql-tag'
import dynamic from 'next/dynamic'
import { useDebouncedCallback } from 'use-debounce'
import {
  CoverLetterItemDesktopBody_CoverLetterFragment,
  CoverLetterItemDesktopBody_CurrentUserFragment,
} from 'generated/graphql'
import { PageLayoutContext } from 'components/PageLayout'
import CoverLetterDetail from './CoverLetterDetail'

const SideBar = dynamic(() => import('./SideBar'), {
  ssr: false,
  loading: function SideBarLoading() {
    return (
      <Box
        width="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    )
  },
})

const useStyles = makeStyles(() => ({
  pcWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  container: {
    display: 'flex',
    minHeight: 0,
  },
  simplePageContainer: {
    display: 'block',
    flex: 1,
    minHeight: 'initial',
  },
}))

interface CoverLetterItemDesktopBodyProps {
  isSimplePage?: boolean
  coverLetter?: CoverLetterItemDesktopBody_CoverLetterFragment
  currentUser?: CoverLetterItemDesktopBody_CurrentUserFragment
  listLink?: string
  coverLetterId?: string
  highlight?: string[] | null | undefined
}

const CoverLetterItemDesktopBody: FC<CoverLetterItemDesktopBodyProps> = ({
  isSimplePage,
  coverLetter,
  currentUser,
  listLink,
  highlight,
}) => {
  const classes = useStyles()

  const [disableWheel, setDisableWheel] = useState(false)
  const { setShowHeader } = useContext(PageLayoutContext)

  const [debounceSetShowHeader] = useDebouncedCallback(setShowHeader, 100, {
    maxWait: 200,
  })

  const onWheel = useCallback<WheelEventHandler>(
    (event) => {
      debounceSetShowHeader(event.deltaY < 0)
    },

    [],
  )

  const handleChildScrollEnter = useCallback(() => {
    setShowHeader(true)
  }, [])
  const handleChildScrollLeave = useCallback(() => {
    setShowHeader(false)
  }, [])

  const handleScrollTop = useCallback(() => {
    setDisableWheel(false)
  }, [])
  const handleScrollMove = useCallback(() => {
    setDisableWheel(true)
  }, [])

  return (
    <div
      className={classes.pcWrapper}
      onWheel={disableWheel || isSimplePage ? undefined : onWheel}
    >
      <Container
        maxWidth="lg"
        className={clsx(classes.container, {
          [classes.simplePageContainer]: isSimplePage,
        })}
      >
        {isSimplePage ? (
          <CoverLetterDetail
            isSimplePage={isSimplePage}
            coverLetter={coverLetter ?? undefined}
            currentUser={currentUser ?? undefined}
            highlight={highlight}
          />
        ) : (
          <>
            <Box width="440px" display="flex">
              <SideBar
                onScrollEnter={handleChildScrollEnter}
                onScrollLeave={handleChildScrollLeave}
                currentUser={currentUser}
              />
            </Box>
            <CoverLetterDetail
              listLink={listLink}
              coverLetter={coverLetter ?? undefined}
              currentUser={currentUser ?? undefined}
              onScrollEnter={handleChildScrollEnter}
              onScrollLeave={handleChildScrollLeave}
              onScrollTop={handleScrollTop}
              onScrollMove={handleScrollMove}
              highlight={highlight}
            />
          </>
        )}
      </Container>
    </div>
  )
}

export default CoverLetterItemDesktopBody

gql`
  fragment CoverLetterItemDesktopBody_currentUser on User {
    ...CoverLetterDetail_currentUser
  }
  fragment CoverLetterItemDesktopBody_coverLetter on CoverLetter {
    ...CoverLetterDetail_coverLetter
  }
`
